import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { v2ConnectorsService } from '../services/v2ConnectorsService';
import { connectorTypesService } from '../services/connectorTypesService';
import ConfirmModal from './shared/ConfirmModal';
import './Admin.css';

const V2Connectors = () => {
    const [connectors, setConnectors] = useState([]);
    const [connectorTypes, setConnectorTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState('');
    const [filterName, setFilterName] = useState('');
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [connectorsResponse, typesResponse] = await Promise.all([
                v2ConnectorsService.list(),
                connectorTypesService.list()
            ]);
            setConnectors(connectorsResponse.connectors || []);
            setConnectorTypes(typesResponse.connectorTypes || []);
            setError(null);
        } catch (err) {
            setError('Failed to load data: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = async () => {
        try {
            setLoading(true);
            const filters = {};
            if (filterType) filters.v2_connector_type_id = filterType;
            if (filterName) filters.name = filterName;

            const response = await v2ConnectorsService.list(filters);
            setConnectors(response.connectors || []);
            setError(null);
        } catch (err) {
            setError('Failed to filter connectors: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClearFilters = () => {
        setFilterType('');
        setFilterName('');
        loadData();
    };

    const handleDelete = (id) => setConfirmDeleteId(id);

    const doDelete = async () => {
        const id = confirmDeleteId;
        setConfirmDeleteId(null);
        try {
            await v2ConnectorsService.delete(id);
            loadData();
        } catch (err) {
            setError('Failed to delete connector: ' + err.message);
        }
    };

    if (loading) return <div className="loading">Loading...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="admin-container">
            <div className="admin-header">
                <h2>V2 Connectors</h2>
                <Link to="/admin/v2-connectors/new" className="btn btn-primary">
                    Add New Connector
                </Link>
            </div>

            <div className="filter-section">
                <div className="filter-group">
                    <label>Connector Type:</label>
                    <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                    >
                        <option value="">All Types</option>
                        {connectorTypes.map(type => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Name:</label>
                    <input
                        type="text"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder="Filter by name..."
                    />
                </div>
                <div className="filter-buttons">
                    <button onClick={handleFilter} className="btn btn-primary">Filter</button>
                    <button onClick={handleClearFilters} className="btn btn-secondary">Clear</button>
                </div>
            </div>

            <table className="admin-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Connector Type</th>
                        <th>Marketplace</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {connectors.length === 0 ? (
                        <tr>
                            <td colSpan="5" style={{textAlign: 'center'}}>No connectors found</td>
                        </tr>
                    ) : (
                        connectors.map(connector => (
                            <tr key={connector.id}>
                                <td>{connector.id}</td>
                                <td>{connector.name}</td>
                                <td>{connector.connector_type_name || '-'}</td>
                                <td>{connector.marketplace_name || '-'}</td>
                                <td className="actions">
                                    <Link to={`/admin/v2-connectors/${connector.id}`} className="btn btn-small">
                                        View
                                    </Link>
                                    <Link to={`/admin/v2-connectors/${connector.id}/edit`} className="btn btn-small btn-primary">
                                        Edit
                                    </Link>
                                    <button
                                        onClick={() => handleDelete(connector.id)}
                                        className="btn btn-small btn-danger"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {confirmDeleteId !== null && (
                <ConfirmModal
                    title="Delete connector"
                    message="Are you sure you want to delete this connector? This cannot be undone."
                    confirmLabel="Delete"
                    danger
                    onConfirm={doDelete}
                    onCancel={() => setConfirmDeleteId(null)}
                />
            )}
        </div>
    );
};

export default V2Connectors;
